// DecisionBreakdownRow.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  TableCell,
  TableRow,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PropTypes from 'prop-types';

const numberFmt = (n) =>
  Number.isFinite(n) ? n.toLocaleString() : String(n ?? '');

const DecisionBreakdownRow = ({
  step,
  colSpan = 5,
  detailsVariant = 'table',
}) => {
  const items = Array.isArray(step?.routing_breakdown)
    ? step.routing_breakdown
    : [];
  if (!items.length) return null;

  const { rows, total } = useMemo(() => {
    const t = items.reduce((acc, it) => acc + (Number(it.records) || 0), 0);
    const r = items.map((it) => ({
      ...it,
      share: t ? (Number(it.records) / t) * 100 : 0,
    }));
    return { rows: r, total: t };
  }, [items]);

  // dropdown (menu) state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ p: 0, border: 'none' }}>
        <Box
          sx={{
            pt: 2,
            pb: 1.5,
            px: 2,
            borderTop: '1px dashed rgba(89,101,166,0.25)',
            background: 'linear-gradient(180deg, #f8f9fa 0%, #f1f3f5 100%)',
          }}
        >
          {/* Header with title and total */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#1f2230',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Decision Routing
            </Typography>
            <Chip
              size="small"
              label={`Total Routed: ${numberFmt(total)}`}
              sx={{
                height: 26,
                color: '#fff',
                backgroundColor: '#5965A6',
                fontWeight: 700,
                borderRadius: '12px',
                px: 1.25,
                fontSize: '0.7rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              }}
            />
          </Box>

          {/* Professional table visualization */}
          {detailsVariant === 'table' ? (
            <TableContainer
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid rgba(89,101,166,0.2)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                backgroundColor: '#fff',
                maxWidth: '90%',
              }}
            >
              <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                <TableHead
                  sx={{
                    background:
                      'linear-gradient(135deg, #ececf2 0%, #e0e1e8 100%)',
                    '& th': {
                      color: '#1f2230',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      py: 0.5,
                      px: 1.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid rgba(89,101,166,0.3)',
                    },
                    '& th:not(:last-of-type)': {
                      borderRight: '1px solid rgba(89,101,166,0.2)',
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>Connection Name</TableCell>
                    <TableCell align="right">Records</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((it, idx) => (
                    <TableRow
                      key={it.connection_id}
                      sx={{
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(89,101,166,0.03)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(89,101,166,0.06)',
                        },
                        transition: 'background-color 0.2s ease',
                        '& td': {
                          borderBottom:
                            idx < rows.length - 1
                              ? '1px solid rgba(89,101,166,0.15)'
                              : 'none',
                        },
                        '& td:not(:last-of-type)': {
                          borderRight: '1px solid rgba(89,101,166,0.15)',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: '#111827',
                          py: 0.25,
                          px: 1.5,
                        }}
                      >
                        {it.connection_name}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          color: '#374151',
                          py: 0.25,
                          px: 1.5,
                        }}
                      >
                        {numberFmt(it.records)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleOpen}
                sx={{
                  height: 28,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'rgba(89,101,166,0.35)',
                  color: '#1f2230',
                  '&:hover': { borderColor: 'rgba(89,101,166,0.6)' },
                }}
              >
                Breakdown ({rows.length})
              </Button>

              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {rows.map((it) => (
                  <MenuItem key={it.connection_id} onClick={handleClose}>
                    <ListItemText
                      primary={it.connection_name}
                      secondary={`${numberFmt(it.records)} records (${it.share.toFixed(1)}%)`}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

DecisionBreakdownRow.propTypes = {
  colSpan: PropTypes.number,
  detailsVariant: PropTypes.oneOf(['table', 'menu']),
  step: PropTypes.shape({
    routing_breakdown: PropTypes.arrayOf(
      PropTypes.shape({
        connection_id: PropTypes.string.isRequired,
        connection_name: PropTypes.string.isRequired,
        records: PropTypes.number.isRequired,
      }),
    ),
    step_status: PropTypes.string,
    success_record_count: PropTypes.number,
    failure_record_count: PropTypes.number,
    duration_seconds: PropTypes.number,
  }).isRequired,
};

export default DecisionBreakdownRow;
